import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { sessionStorageManager as storage, cleanupLegacyLocalStorage } from '../lib/storage';
import {
  can as _can,
  hasRole as _hasRole,
  isAdmin as _isAdmin,
  isSuperAdmin as _isSuperAdmin,
  Action,
  ADMIN_ACTIONS,
  ACTION_REQUIRED_LEVEL,
  ORG_ROLE,
  PROJECT_ROLE,
  PROJECT_ROLE_LEVEL,
} from '../lib/auth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// One-time cleanup of auth keys left in localStorage from before
// the migration to per-tab sessionStorage.
cleanupLegacyLocalStorage();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(storage.getToken());
  const [loading, setLoading] = useState(true);

  const fetchCurrentUser = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await api.get('/me');
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      storage.clearToken();
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchCurrentUser();
  }, [fetchCurrentUser]);

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, is_temp_password } = response.data;
    storage.setToken(access_token);
    setToken(access_token);

    // Fetch user data
    const userResponse = await api.get('/me', {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    const userData = { ...userResponse.data, is_temp_password };
    setUser(userData);

    return userData;
  };

  const createOrganization = async (orgName, adminName, adminEmail, adminPassword) => {
    const response = await api.post('/auth/create-organization', {
      org_name: orgName,
      admin_name: adminName,
      admin_email: adminEmail,
      admin_password: adminPassword
    });
    const { access_token } = response.data;
    storage.setToken(access_token);
    setToken(access_token);

    // Fetch user data
    const userResponse = await api.get('/me', {
      headers: { Authorization: `Bearer ${access_token}` }
    });
    setUser(userResponse.data);

    return userResponse.data;
  };

  const changePassword = async (currentPassword, newPassword) => {
    await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    // Update local user state to reflect password is no longer temp
    setUser(prev => prev ? { ...prev, is_temp_password: false, account_status: 'active' } : null);
  };

  const updateProfile = async (name) => {
    await api.patch('/auth/profile', { name });
    setUser(prev => prev ? { ...prev, name } : null);
  };

  const logout = () => {
    storage.clearAuthStorage();
    setToken(null);
    setUser(null);
  };

  // RBAC v2 helpers — delegate to pure lib/auth.js functions.
  //
  // The ORG tier (.role) and the PLANNER grant (.is_planner) come from /api/me.
  // The PROJECT axis (plan/collaborate/view) is per-project and is NOT held in
  // auth state — callers pass the caller's role on the active project as
  // `projectRole`. `can` is the single decision point, mirroring backend
  // core/rbac.py value-for-value; the planner grant is injected here so call
  // sites stay unchanged.
  const can = useCallback(
    (action, projectRole = null) =>
      _can(action, { orgRole: user?.role, isPlanner: !!user?.is_planner, projectRole }),
    [user]
  );

  const hasRole = useCallback((roles) => _hasRole(user?.role, roles), [user]);
  const isSuperAdmin = useCallback(() => _isSuperAdmin(user?.role), [user]);
  const isAdmin = useCallback(() => _isAdmin(user?.role), [user]);
  // The planner grant (composable with any tier; super_admin implies it).
  const isPlanner = useCallback(
    () => !!user && (!!user.is_planner || user.role === ORG_ROLE.SUPER_ADMIN),
    [user]
  );

  // Back-compat coarse UI gates for components not yet migrated to the project
  // axis (sidebar nav util, AI widget, projects list, import/export, AI dialog).
  // These only drive what affordances to SHOW — real authorization is enforced by
  // ProtectedRoute, ProjectContext.can() (per-project), and the backend (403/404).
  // Page visibility: any authenticated user may reach the route guard, which then
  // does the precise check.
  const canAccessPage = useCallback((_page) => !!user, [user]);
  // Feature editing: org-privileged roles always; members may edit where their
  // per-project role allows (refined by ProjectContext.can() in project-aware
  // components and enforced by the backend). Read-only is decided per project, not here.
  const canEditFeature = useCallback(
    (_feature) => !!user && (user.role === 'super_admin' || user.role === 'admin' || user.role === 'member'),
    [user]
  );

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token && !!user,
    login,
    createOrganization,
    changePassword,
    updateProfile,
    logout,
    // RBAC v2 helpers (sourced from lib/auth.js)
    can,
    hasRole,
    isSuperAdmin,
    isAdmin,
    isPlanner,
    // Back-compat coarse UI gates (real enforcement: ProtectedRoute + ProjectContext.can() + backend)
    canAccessPage,
    canEditFeature,
    // Re-exported constants so consumers reference the policy in one place.
    Action,
    ADMIN_ACTIONS,
    ACTION_REQUIRED_LEVEL,
    ORG_ROLE,
    PROJECT_ROLE,
    PROJECT_ROLE_LEVEL,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
